
// src/theme/MuiBridge.js
import React, { useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from './ThemeContext';

export default function MuiBridge({ children }) {
  const { isDark } = useTheme();

  const muiTheme = useMemo(() => {
    // navy-ish dark set
    const darkBackgroundDefault = '#0B1220'; // deep navy
    const darkBackgroundPaper   = '#0F172A'; // slate-900
    const darkTextPrimary       = '#E6ECFF'; // soft blue-white
    const darkTextSecondary     = '#A3B1D1'; // desaturated blue-gray
    const darkDivider           = 'rgba(148,163,184,0.24)'; // slate-400 @ 24%
    const darkHover             = 'rgba(148,163,184,0.08)'; // subtle slate tint

    const base = {
      palette: {
        mode: isDark ? 'dark' : 'light',
        primary: { main: '#6366F1' },  // indigo
        secondary: { main: '#8B5CF6' },// violet
      },
      shape: { borderRadius: 12 },
      components: {
        MuiPaper: { styleOverrides: { root: { transition: 'background-color .2s ease' } } },
        MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
        MuiDataGrid: { styleOverrides: { root: { borderRadius: 12 } } },
      },
    };

    if (!isDark) {
      return createTheme(base);
    }

    // extend for dark navy theme
    return createTheme({
      ...base,
      palette: {
        ...base.palette,
        background: {
          default: darkBackgroundDefault,
          paper: darkBackgroundPaper,
        },
        text: {
          primary: darkTextPrimary,
          secondary: darkTextSecondary,
        },
        divider: darkDivider,
        action: {
          hover: darkHover,
          selected: 'rgba(148,163,184,0.16)',
          disabled: 'rgba(148,163,184,0.38)',
          disabledBackground: 'rgba(148,163,184,0.12)',
          focus: 'rgba(99,102,241,0.36)', // faint indigo focus
          activatedOpacity: 0.12,
          hoverOpacity: 0.08,
          selectedOpacity: 0.12,
          disabledOpacity: 0.38,
          focusOpacity: 0.24,
        },
      },
      components: {
        ...base.components,
        MuiTooltip: {
          styleOverrides: {
            tooltip: ({ theme }) => ({
              backgroundColor: alpha(theme.palette.background.paper, 0.98),
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
            }),
          },
        },
        MuiCard: {
          styleOverrides: {
            root: ({ theme }) => ({
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }),
          },
        },
      },
    });
  }, [isDark]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
